import { parseCartAttributesArray, cartAttributesObjectToArray, } from "@novel/shared/utils/parseCartAttributes";
import { getHeadlessCheckoutIdAndCartId, pollHeadlessCheckoutIdAndOrCartId, } from "@vendor-storefront-global/tokenGating/utils/cartUtils/cartOperations/headless/getHeadlessCheckoutIdAndCartId";
import merge from "lodash/merge";
const checkoutFragments = `
    fragment DiscountApplicationFragment on DiscountApplication {
        __typename
        targetSelection
        allocationMethod
        targetType
        value {
            ... on MoneyV2 {
                amount
                currencyCode
            }
            ... on PricingPercentageValue {
                percentage
            }
        }
        ... on ManualDiscountApplication {
            title
            description
        }
        ... on DiscountCodeApplication {
            code
            applicable
        }
        ... on ScriptDiscountApplication {
            title
        }
        ... on AutomaticDiscountApplication {
            title
        }
    },
    fragment CheckoutFragment on Checkout {
        webUrl
        discountApplications(first: 10) {
            pageInfo {
                hasNextPage
                hasPreviousPage
            }
            edges {
                node {
                    __typename
                    ...DiscountApplicationFragment
                }
            }
        }
        customAttributes {
            key
            value
        }
    },
`;
const userErrorFragments = `
    fragment UserErrorFragment on UserError {
        field
        message
    },
    fragment CheckoutUserErrorFragment on CheckoutUserError {
        field
        message
        code
    },
`;
const cartAttributes = `
    id
    checkoutUrl
    attributes{
        key
        value
    }
    discountCodes{
        code
        applicable
    }
`;
const storefrontCheckoutQuery = `
    ${checkoutFragments}
    query ($id: ID!) {
        node(id: $id) {
            __typename
            ...CheckoutFragment
        }
    }
`;
const storefontCartQuery = `query ($id: ID!) {
    cart(id: $id){
        ${cartAttributes}
    }
}`;
const storefrontUpdateCartAttributesMutation = `
    mutation cartAttributesUpdate($attributes: [AttributeInput!]!, $cartId: ID!) {
        cartAttributesUpdate(attributes: $attributes, cartId: $cartId) {
            cart {
                ${cartAttributes}
            }
            userErrors {
                field
                message
            }
        }
    }
  `;
const storefrontCartDiscountCodesUpdateMutation = `
    mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!] ) {
        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
            cart {
                ${cartAttributes}
            }
            userErrors {
                field
                message
            }
        }
    }
  `;
export async function getCartAttributesHeadless(shopifyDomain, storefrontAccessToken, cartId) {
    return await storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, JSON.stringify({
        query: storefontCartQuery,
        variables: {
            id: cartId,
        },
    })).then((res) => {
        var _a;
        const { id, checkoutUrl, attributes = [], discountCodes = [] } = ((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.cart) || {};
        return {
            isHeadless: true,
            checkoutId: checkoutUrl,
            customAttributes: parseCartAttributesArray(attributes),
            discountApplications: discountCodes.map((discount) => discount.code),
        };
    });
}
export async function setCartAttributesHeadless(shopifyDomain, storefrontAccessToken, customAttributes, cartId) {
    return await storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, JSON.stringify({
        query: storefrontUpdateCartAttributesMutation,
        variables: {
            cartId,
            attributes: cartAttributesObjectToArray(customAttributes),
        },
    })).then((res) => {
        var _a, _b, _c;
        const userErrors = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.cartAttributesUpdate) === null || _b === void 0 ? void 0 : _b.userErrors;
        if (userErrors && Array.isArray(userErrors) ? userErrors === null || userErrors === void 0 ? void 0 : userErrors.length : userErrors) {
            if (Array.isArray(userErrors)) {
                throw new Error(userErrors
                    .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                    .filter(Boolean)
                    .join(", ") || "Unknown GraphQl error");
            }
            throw new Error(userErrors);
        }
        const { id, checkoutUrl, attributes = [], discountCodes = [], } = ((_c = res === null || res === void 0 ? void 0 : res.data) === null || _c === void 0 ? void 0 : _c.cartAttributesUpdate) || {};
        return {
            isHeadless: true,
            cartId: id,
            customAttributes: parseCartAttributesArray(attributes),
            discountApplications: discountCodes.map((discount) => discount === null || discount === void 0 ? void 0 : discount.code),
        };
    });
}
export async function getCheckoutAttributesHeadless(shopifyDomain, storefrontAccessToken, checkoutId) {
    return await storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, JSON.stringify({
        query: storefrontCheckoutQuery,
        variables: {
            id: checkoutId,
        },
    })).then((res) => {
        var _a;
        const { webUrl, customAttributes = [], discountApplications = {} } = ((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.node) || {};
        return {
            isHeadless: true,
            checkoutId: extractCheckoutIdFromWebUrl(webUrl),
            customAttributes: parseCartAttributesArray(customAttributes),
            discountApplications: ((discountApplications === null || discountApplications === void 0 ? void 0 : discountApplications.edges) || []).map((edgeContainer) => ((edgeContainer === null || edgeContainer === void 0 ? void 0 : edgeContainer.node.title) || (edgeContainer === null || edgeContainer === void 0 ? void 0 : edgeContainer.node.code))),
        };
    });
}
export async function getCartHeadless(shopifyDomain, storefrontAccessToken) {
    const ids = getHeadlessCheckoutIdAndCartId();
    if (ids === null || ids === void 0 ? void 0 : ids.checkoutId) {
        return await getCheckoutAttributesHeadless(shopifyDomain, storefrontAccessToken, ids.checkoutId);
    }
    else if (ids === null || ids === void 0 ? void 0 : ids.cartId) {
        return await getCartAttributesHeadless(shopifyDomain, storefrontAccessToken, ids.cartId);
    }
    return {
        isHeadless: true,
        cartId: "",
        checkoutId: "",
        customAttributes: parseCartAttributesArray([]),
        discountApplications: [],
    };
}
const storefrontUpdateCustomAttributesMutation = `
    ${checkoutFragments}
    ${userErrorFragments}
    mutation checkoutAttributesUpdateV2($checkoutId: ID!, $input: CheckoutAttributesUpdateV2Input!) {
        checkoutAttributesUpdateV2(checkoutId: $checkoutId, input: $input) {
            checkout {
                ...CheckoutFragment
            }
            userErrors {
                ...UserErrorFragment
            }
            checkoutUserErrors {
                ...CheckoutUserErrorFragment
            }
        }
    }
`;
export async function setCheckoutAttributesHeadless(shopifyDomain, storefrontAccessToken, customAttributes, checkoutId) {
    return await storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, JSON.stringify({
        query: storefrontUpdateCustomAttributesMutation,
        variables: {
            checkoutId,
            input: {
                customAttributes: cartAttributesObjectToArray(customAttributes),
            },
        },
    })).then((res) => {
        var _a, _b, _c, _d, _e, _f;
        const userErrors = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.checkoutAttributesUpdateV2) === null || _b === void 0 ? void 0 : _b.userErrors;
        if (userErrors && Array.isArray(userErrors) ? userErrors === null || userErrors === void 0 ? void 0 : userErrors.length : userErrors) {
            if (Array.isArray(userErrors)) {
                throw new Error(userErrors
                    .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                    .filter(Boolean)
                    .join(", ") || "Unknown GraphQl error");
            }
            throw new Error(userErrors);
        }
        const checkoutUserErrors = (_d = (_c = res === null || res === void 0 ? void 0 : res.data) === null || _c === void 0 ? void 0 : _c.checkoutAttributesUpdateV2) === null || _d === void 0 ? void 0 : _d.checkoutUserErrors;
        if (checkoutUserErrors && Array.isArray(checkoutUserErrors)
            ? checkoutUserErrors === null || checkoutUserErrors === void 0 ? void 0 : checkoutUserErrors.length
            : checkoutUserErrors) {
            if (Array.isArray(checkoutUserErrors)) {
                throw new Error(checkoutUserErrors
                    .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                    .filter(Boolean)
                    .join(", ") || "Unknown GraphQl error");
            }
            throw new Error(checkoutUserErrors);
        }
        const { webUrl, customAttributes = [], discountApplications = {}, } = ((_f = (_e = res === null || res === void 0 ? void 0 : res.data) === null || _e === void 0 ? void 0 : _e.checkoutAttributesUpdateV2) === null || _f === void 0 ? void 0 : _f.checkout) || {};
        return {
            isHeadless: true,
            checkoutId: extractCheckoutIdFromWebUrl(webUrl),
            customAttributes: parseCartAttributesArray(customAttributes),
            discountApplications: ((discountApplications === null || discountApplications === void 0 ? void 0 : discountApplications.edges) || [])
                .map((edgeContainer) => ((edgeContainer === null || edgeContainer === void 0 ? void 0 : edgeContainer.node.title) || (edgeContainer === null || edgeContainer === void 0 ? void 0 : edgeContainer.node.code)))
                .filter(Boolean),
        };
    });
}
const storefrontApplyDiscountCodeMutation = `
    ${checkoutFragments}
    ${userErrorFragments}
    mutation checkoutDiscountCodeApplyV2($checkoutId: ID!, $discountCode: String!) {
        checkoutDiscountCodeApplyV2(checkoutId: $checkoutId, discountCode: $discountCode) {
            checkout {
                ...CheckoutFragment
            }
            userErrors {
                ...UserErrorFragment
            }
            checkoutUserErrors {
                ...CheckoutUserErrorFragment
            }
        }
    }
`;
export async function applyDiscountCodeHeadless({ shopifyDomain, storefrontAccessToken, discountCode, onLoading, }) {
    let ids = getHeadlessCheckoutIdAndCartId();
    if (!ids) {
        // assumes that adding item to cart will create and set checkout or cart id in storage
        console.error("Need checkout ID to apply balance to cart");
        ids = await pollHeadlessCheckoutIdAndOrCartId(async () => {
            const loadStorefrontStyleSheetPromise = import("@vendor-storefront-global/tokenGating/gateApp/styles/loadStorefrontStyleSheet").then(({ loadStorefrontStyleSheet }) => loadStorefrontStyleSheet());
            // used to handle that the display of the UX of the toast is async
            const infoToast = await import("@novel/shared/utils/toastUtils").then(({ infoToast }) => infoToast);
            await loadStorefrontStyleSheetPromise;
            infoToast({
                content: "Discount will be applied as soon as you have added an item to the cart.",
            });
        });
    }
    if (ids === null || ids === void 0 ? void 0 : ids.checkoutId) {
        onLoading === null || onLoading === void 0 ? void 0 : onLoading();
        return applyCheckoutDiscountCodeHeadless(shopifyDomain, storefrontAccessToken, discountCode, ids.checkoutId);
    }
    if (ids === null || ids === void 0 ? void 0 : ids.cartId) {
        onLoading === null || onLoading === void 0 ? void 0 : onLoading();
        return applyCartDiscountCodeHeadless(shopifyDomain, storefrontAccessToken, discountCode, ids.cartId);
    }
}
export async function applyCartDiscountCodeHeadless(shopifyDomain, storefrontAccessToken, discountCode, cartId) {
    return await storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, JSON.stringify({
        query: storefrontCartDiscountCodesUpdateMutation,
        variables: {
            cartId,
            discountCodes: [discountCode],
        },
    })).then((res) => {
        var _a, _b, _c;
        const userErrors = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.cartDiscountCodeUpdate) === null || _b === void 0 ? void 0 : _b.userErrors;
        if (userErrors && Array.isArray(userErrors) ? userErrors === null || userErrors === void 0 ? void 0 : userErrors.length : userErrors) {
            if (Array.isArray(userErrors)) {
                throw new Error(userErrors
                    .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                    .filter(Boolean)
                    .join(", ") || "Unknown GraphQl error");
            }
            throw new Error(userErrors);
        }
        const { id, checkoutUrl, attributes = [], discounts = [], } = ((_c = res === null || res === void 0 ? void 0 : res.data) === null || _c === void 0 ? void 0 : _c.cartDiscountCodeUpdate) || {};
        return {
            isHeadless: true,
            cartId: id,
            customAttributes: parseCartAttributesArray(attributes),
            discountApplications: discounts.map((discount) => discount.code),
        };
    });
}
export async function applyCheckoutDiscountCodeHeadless(shopifyDomain, storefrontAccessToken, discountCode, checkoutId) {
    return await storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, JSON.stringify({
        query: storefrontApplyDiscountCodeMutation,
        variables: {
            checkoutId,
            discountCode,
        },
    })).then((res) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const userErrors = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.checkoutDiscountCodeApplyV2) === null || _b === void 0 ? void 0 : _b.userErrors;
        if (userErrors && Array.isArray(userErrors) ? userErrors === null || userErrors === void 0 ? void 0 : userErrors.length : userErrors) {
            if (Array.isArray(userErrors)) {
                throw new Error(userErrors
                    .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                    .filter(Boolean)
                    .join(", ") || "Unknown GraphQl error");
            }
            throw new Error(userErrors);
        }
        const checkoutUserErrors = (_d = (_c = res === null || res === void 0 ? void 0 : res.data) === null || _c === void 0 ? void 0 : _c.checkoutDiscountCodeApplyV2) === null || _d === void 0 ? void 0 : _d.checkoutUserErrors;
        if (checkoutUserErrors && Array.isArray(checkoutUserErrors)
            ? checkoutUserErrors === null || checkoutUserErrors === void 0 ? void 0 : checkoutUserErrors.length
            : checkoutUserErrors) {
            if (Array.isArray(checkoutUserErrors)) {
                throw new Error(checkoutUserErrors
                    .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                    .filter(Boolean)
                    .join(", ") || "Unknown GraphQl error");
            }
            throw new Error(checkoutUserErrors);
        }
        const { webUrl, customAttributes = [], discountApplications = {}, } = ((_f = (_e = res === null || res === void 0 ? void 0 : res.data) === null || _e === void 0 ? void 0 : _e.checkoutDiscountCodeApplyV2) === null || _f === void 0 ? void 0 : _f.checkout) || {};
        if (!((_g = discountApplications === null || discountApplications === void 0 ? void 0 : discountApplications.edges) === null || _g === void 0 ? void 0 : _g.length)) {
            throw new Error("Please add applicable item(s) to your cart before applying discount");
        }
        return {
            isHeadless: true,
            checkoutId: extractCheckoutIdFromWebUrl(webUrl),
            customAttributes: parseCartAttributesArray(customAttributes),
            discountApplications: ((discountApplications === null || discountApplications === void 0 ? void 0 : discountApplications.edges) || [])
                .map((edgeContainer) => ((edgeContainer === null || edgeContainer === void 0 ? void 0 : edgeContainer.node.title) || (edgeContainer === null || edgeContainer === void 0 ? void 0 : edgeContainer.node.code)))
                .filter(Boolean),
        };
    });
}
export async function upsertCartAttributesHeadless(shopifyDomain, storefrontAccessToken, updatedCustomAttributes) {
    const ids = getHeadlessCheckoutIdAndCartId();
    const { customAttributes } = await getCartHeadless(shopifyDomain, storefrontAccessToken);
    let updatedCart;
    if (ids === null || ids === void 0 ? void 0 : ids.cartId) {
        updatedCart = await setCartAttributesHeadless(shopifyDomain, storefrontAccessToken, merge(customAttributes, updatedCustomAttributes), ids.cartId);
    }
    if (ids === null || ids === void 0 ? void 0 : ids.checkoutId) {
        updatedCart = await setCheckoutAttributesHeadless(shopifyDomain, storefrontAccessToken, merge(customAttributes, updatedCustomAttributes), ids.checkoutId);
    }
    return updatedCart;
}
export async function removeDiscountCodeHeadless(shopifyDomain, storefrontAccessToken) {
    const ids = getHeadlessCheckoutIdAndCartId();
    if (ids === null || ids === void 0 ? void 0 : ids.checkoutId) {
        await storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, JSON.stringify({
            query: storefrontApplyDiscountCodeMutation,
            variables: {
                checkoutId: ids.checkoutId,
                discountCode: "",
            },
        })).then((res) => {
            var _a, _b, _c, _d;
            const userErrors = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.checkoutDiscountCodeApplyV2) === null || _b === void 0 ? void 0 : _b.userErrors;
            if (userErrors && Array.isArray(userErrors) ? userErrors === null || userErrors === void 0 ? void 0 : userErrors.length : userErrors) {
                if (Array.isArray(userErrors)) {
                    throw new Error(userErrors
                        .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                        .filter(Boolean)
                        .join(", ") || "Unknown GraphQl error");
                }
                throw new Error(userErrors);
            }
            const checkoutUserErrors = (_d = (_c = res === null || res === void 0 ? void 0 : res.data) === null || _c === void 0 ? void 0 : _c.checkoutDiscountCodeApplyV2) === null || _d === void 0 ? void 0 : _d.checkoutUserErrors;
            if (checkoutUserErrors && Array.isArray(checkoutUserErrors)
                ? checkoutUserErrors === null || checkoutUserErrors === void 0 ? void 0 : checkoutUserErrors.length
                : checkoutUserErrors) {
                if (Array.isArray(checkoutUserErrors)) {
                    throw new Error(checkoutUserErrors
                        .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                        .filter(Boolean)
                        .join(", ") || "Unknown GraphQl error");
                }
                throw new Error(checkoutUserErrors);
            }
        });
    }
    if (ids === null || ids === void 0 ? void 0 : ids.cartId) {
        await storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, JSON.stringify({
            query: storefrontCartDiscountCodesUpdateMutation,
            variables: {
                cartId: ids.cartId,
                discountCodes: [""],
            },
        })).then((res) => {
            var _a, _b;
            const userErrors = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.cartDiscountCodeUpdate) === null || _b === void 0 ? void 0 : _b.userErrors;
            if (userErrors && Array.isArray(userErrors) ? userErrors === null || userErrors === void 0 ? void 0 : userErrors.length : userErrors) {
                if (Array.isArray(userErrors)) {
                    throw new Error(userErrors
                        .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                        .filter(Boolean)
                        .join(", ") || "Unknown GraphQl error");
                }
                throw new Error(userErrors);
            }
        });
    }
}
export async function storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, bodyStr, shopifyApiVersion) {
    return await fetch(`https://${shopifyDomain}/api/${shopifyApiVersion ? shopifyApiVersion : "2024-01"}/graphql`, {
        headers: {
            accept: "application/json",
            "accept-language": "*",
            "content-type": "application/json",
            "x-shopify-storefront-access-token": storefrontAccessToken,
        },
        referrerPolicy: "strict-origin-when-cross-origin",
        method: "POST",
        mode: "cors",
        credentials: "omit",
        body: bodyStr,
    })
        .then((res) => res.json())
        .then((res) => {
        if (res.errors) {
            if (Array.isArray(res.errors)) {
                throw new Error(res.errors
                    .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                    .filter(Boolean)
                    .join(", ") || "Unknown GraphQl error");
            }
            throw new Error(res.errors);
        }
        return res;
    });
}
export function extractCheckoutIdFromWebUrl(webUrl) {
    const pathName = new URL(webUrl).pathname;
    const segmentAfterCheckouts = pathName.split("/checkouts/").pop();
    if (!segmentAfterCheckouts) {
        throw new Error("'/checkouts/' segment should exist");
    }
    const firstSegmentAfterCheckout = segmentAfterCheckouts.split("/")[0];
    if (!firstSegmentAfterCheckout) {
        throw new Error("checkout token should exist");
    }
    return firstSegmentAfterCheckout;
}
const storefrontCheckoutGiftCardsAppendMutation = `
${checkoutFragments}
${userErrorFragments}
mutation checkoutGiftCardsAppend($checkoutId: ID!, $giftCardCodes: [String!]!) {
    checkoutGiftCardsAppend(checkoutId: $checkoutId, giftCardCodes: $giftCardCodes) {
        checkout {
            ...CheckoutFragment
        }
        userErrors {
            ...UserErrorFragment
        }
        checkoutUserErrors {
            ...CheckoutUserErrorFragment
        }
    }
  }`;
export async function applyStoreCreditHeadless({ shopifyDomain, storefrontAccessToken, storeCreditCode, onLoading, }) {
    let ids = getHeadlessCheckoutIdAndCartId();
    if (!ids || !(ids === null || ids === void 0 ? void 0 : ids.checkoutId)) {
        //need checkout ID, won't work with applyStoreCreditHeadless
        console.error("Need checkout ID to apply balance to cart");
        ids = await pollHeadlessCheckoutIdAndOrCartId(async () => {
            const loadStorefrontStyleSheetPromise = import("@vendor-storefront-global/tokenGating/gateApp/styles/loadStorefrontStyleSheet").then(({ loadStorefrontStyleSheet }) => loadStorefrontStyleSheet());
            // used to handle that the display of the UX of the toast is async
            const infoToast = await import("@novel/shared/utils/toastUtils").then(({ infoToast }) => infoToast);
            await loadStorefrontStyleSheetPromise;
            infoToast({
                content: "Discount will be applied as soon as you have added an item to the cart.",
            });
        });
    }
    if (ids === null || ids === void 0 ? void 0 : ids.checkoutId) {
        if (onLoading) {
            onLoading();
        }
        return await storefrontGraphQlCall(shopifyDomain, storefrontAccessToken, JSON.stringify({
            query: storefrontCheckoutGiftCardsAppendMutation,
            variables: {
                checkoutId: ids.checkoutId,
                giftCardCodes: [storeCreditCode],
            },
        })).then((res) => {
            var _a, _b;
            const userErrors = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.cartDiscountCodeUpdate) === null || _b === void 0 ? void 0 : _b.userErrors;
            if (userErrors && Array.isArray(userErrors) ? userErrors === null || userErrors === void 0 ? void 0 : userErrors.length : userErrors) {
                if (Array.isArray(userErrors)) {
                    throw new Error(userErrors
                        .map((error) => (error === null || error === void 0 ? void 0 : error.message) && error.message.trim && error.message.trim())
                        .filter(Boolean)
                        .join(", ") || "Unknown GraphQl error");
                }
                throw new Error(userErrors);
            }
        });
    }
}
